/**
 * Single source of truth for the architecture hero story.
 *
 * Both the 3D world (architectureScene.ts) and the text layer (ArchitectureHero.tsx)
 * read this file, so what the visitor reads and what the 3D scene highlights can never
 * drift apart. It has no three.js dependency on purpose.
 *
 * Every scene is split into "beats". A beat is one sentence of the story and it owns:
 *   - a window of scroll progress,
 *   - a list of focus ids the 3D scene lights up while the beat is active,
 *   - the line of copy that types out in the caption.
 */

export type LayerId = "foundation" | "extensions" | "products" | "proof";

export interface StoryBeat {
  label: string;
  text: string;
  /** Ids registered by the 3D scene (see addFocus in architectureScene.ts). */
  focus: string[];
}

export interface StoryScene {
  eyebrow: string;
  title: string;
  description: string;
  layers: LayerId[];
  beats: StoryBeat[];
  note?: string;
}

export interface StoryState {
  scene: number;
  /** -1 when the scene has no beats (the intro). */
  beat: number;
}

export const SCENES: StoryScene[] = [
  {
    eyebrow: "00 / OVERVIEW",
    title: "Governed intelligence. Built once. Reused everywhere.",
    description: "One shared foundation, parallel business extensions, and products that connect and consume.",
    layers: ["foundation"],
    beats: [],
  },
  {
    eyebrow: "01 / THE FOUNDATION",
    title: "Shared meaning, defined once.",
    description: "Four governed layers sit under every product. Define a concept here and the whole enterprise inherits it.",
    layers: ["foundation"],
    beats: [
      {
        label: "Canonical entities",
        text: "Customer, order, product: one identity and one definition for each concept, whatever each system calls it.",
        focus: ["entities"],
      },
      {
        label: "Metadata and rules",
        text: "Every definition carries an owner, a version and the quality rules it has to pass.",
        focus: ["metadata", "rules"],
      },
      {
        label: "Provenance",
        text: "Every value traces back to the source system and the step that produced it.",
        focus: ["provenance"],
      },
    ],
  },
  {
    eyebrow: "02 / PARALLEL EXTENSIONS",
    title: "Inherit the foundation. Add local context.",
    description: "Each business unit and region extends the shared model instead of copying it.",
    layers: ["extensions"],
    beats: [
      {
        label: "Inherit",
        text: "BU A and BU B receive the shared definitions through governed links.",
        focus: ["bu-0", "bu-1"],
      },
      {
        label: "Extend",
        text: "Region 1 and Region 2 add their own terms, mappings and rules on top.",
        focus: ["bu-2", "bu-3"],
      },
      {
        label: "Stay aligned",
        text: "Every local concept keeps a reference to the canonical concept it extends.",
        focus: ["bu-0", "bu-1", "bu-2", "bu-3"],
      },
    ],
  },
  {
    eyebrow: "03 / GOVERNED BY DESIGN",
    title: "Extend without losing control.",
    description: "Business-owned additions move through draft, validation and publication without overwriting enterprise meaning.",
    layers: ["extensions"],
    note: "Illustrative governance states",
    beats: [
      {
        label: "Draft",
        text: "A business owner proposes a local term, mapping or rule.",
        focus: ["lane-0"],
      },
      {
        label: "Validate",
        text: "Quality rules and the shared ontology check it for conflicts.",
        focus: ["lane-1"],
      },
      {
        label: "Publish",
        text: "Approved changes are versioned and linked back to the foundation, never written over it.",
        focus: ["lane-2"],
      },
    ],
  },
  {
    eyebrow: "04 / REUSABLE INTELLIGENCE",
    title: "Build the product. Reuse the foundation.",
    description: "Products connect to published capabilities instead of rebuilding semantic infrastructure.",
    layers: ["products"],
    beats: [
      {
        label: "CX",
        text: "Customer experience reads the same customer in every channel.",
        focus: ["product-0"],
      },
      {
        label: "Operations",
        text: "Operations work from the same assets, rules and definitions.",
        focus: ["product-1"],
      },
      {
        label: "Analytics",
        text: "Analytics measure what the business actually means.",
        focus: ["product-2"],
      },
    ],
  },
  {
    eyebrow: "05 / FROM DATA TO DECISION",
    title: "Trace every result to its source.",
    description: "Follow a record through ingestion, shared definitions and validation. The product receives a versioned result with its lineage intact.",
    layers: ["proof"],
    note: "No automated action is implied",
    beats: [
      {
        label: "Ingest and normalize",
        text: "A record enters from a source system and its fields are mapped to canonical terms.",
        focus: ["station-0", "station-1", "station-2"],
      },
      {
        label: "Resolve and validate",
        text: "Entities are linked to known references and the quality rules are checked.",
        focus: ["station-3", "station-4"],
      },
      {
        label: "Publish and consume",
        text: "The versioned result reaches the product with its lineage intact.",
        focus: ["station-5", "station-6"],
      },
    ],
  },
];

export const LAYERS: Array<{ id: LayerId; name: string; hint: string; scene: number }> = [
  { id: "foundation", name: "Foundation", hint: "Shared meaning", scene: 1 },
  { id: "extensions", name: "Extensions", hint: "Local context", scene: 2 },
  { id: "products", name: "Products", hint: "Reuse", scene: 4 },
  { id: "proof", name: "Data flow", hint: "Proof", scene: 5 },
];

/** Height of the scroll track, in small-viewport heights. */
export const TRACK_SVH = 1600;

/** Scroll progress where each scene starts (scene 5 runs to 1). */
export const B = [0, 0.1, 0.22, 0.34, 0.46, 0.58];

/** Beats start just after the scene's camera entry window, so copy and 3D arrive together. */
const BEAT_LEAD = 0.03;

export const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

export function sceneAt(progress: number) {
  const p = clamp01(progress);
  let scene = 0;
  for (let i = 1; i < B.length; i += 1) if (p >= B[i]) scene = i;
  return scene;
}

export function sceneRange(scene: number): [number, number] {
  return [B[scene], scene + 1 < B.length ? B[scene + 1] : 1];
}

/** Progress window of one beat. */
export function beatWindow(scene: number, beat: number): [number, number] {
  const count = Math.max(1, SCENES[scene].beats.length);
  const [from, to] = sceneRange(scene);
  const start = from + BEAT_LEAD;
  const length = (to - start) / count;
  return [start + beat * length, start + (beat + 1) * length];
}

export function beatAt(progress: number): StoryState {
  const p = clamp01(progress);
  const scene = sceneAt(p);
  const count = SCENES[scene].beats.length;
  if (count === 0) return { scene, beat: -1 };
  const start = beatWindow(scene, 0)[0];
  const end = beatWindow(scene, count - 1)[1];
  const t = (p - start) / (end - start);
  return { scene, beat: Math.max(0, Math.min(count - 1, Math.floor(t * count))) };
}

/** Progress where a beat is fully settled (camera arrived, wires drawn). */
export function beatAnchor(scene: number, beat: number) {
  const [start, end] = beatWindow(scene, beat);
  return start + (end - start) * 0.75;
}

export function sceneAnchor(scene: number) {
  return scene === 0 ? 0 : beatAnchor(scene, 0);
}

/** Used when the visitor prefers reduced motion: show each beat in its settled state only. */
export function settledProgress(progress: number) {
  const { scene, beat } = beatAt(progress);
  return beat < 0 ? 0.09 : beatAnchor(scene, beat);
}
